import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { notify } from "../../../components/Notification";
import { ErrorState } from "../../../components/States";

import { CreateInvestmentPayload } from "../types";
import { presentedTotalOf, returnBasisOf, settlementPayloadOf } from "../utils";
import {
  useCreateInvestmentMutation,
  useGetInvestmentsHistoryQuery,
  useGetInvestmentsQuery,
  useGetInvestmentsYearsQuery,
} from "../../../services";
import { InvestmentFormValues, InvestmentPanel } from "../panels";
import {
  EmptyInvestmentsState,
  InvestmentsFilters,
  InvestmentsHero,
  InvestmentsHoldings,
  InvestmentsInsights,
  InvestmentsSkeleton,
} from "./components";
import { CreateFab } from "../../../components/CreateFab";
import { SortState } from "./components/InvestmentsHoldings";
import { ProgressBar, ProgressTrack, Wrapper } from "./styles";
import { useResponsive } from "../../../hooks/useResponsive";
import { usePersistedWindow } from "../../../hooks/usePersistedWindow";
import { Options } from "../../../components/Select";
import { BREAKPOINTS, FILTER_OPTIONS } from "../../../utils/constants";
import { WindowType } from "../../../utils/types";
import { formatDate } from "../../../utils";
import { buildInvestmentsGrowth } from "./investmentsGrowth";
import { headlineChangeOf, investmentsTotalsOf } from "./investmentsTotals";
import { ACTIVE_OPTIONS, EMPTY_HISTORY, YEAR_OPTIONS } from "./constants";

// Sentinel period value covering every year, surfaced as the first option.
const ALL_TIME = "all";

export const InvestmentsPage = () => {
  const [, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const [activeOnly, setActiveOnly] = useState(ACTIVE_OPTIONS[0]);
  const [years, setYears] = useState(YEAR_OPTIONS);
  const [periodValue, setPeriodValue] = useState(ALL_TIME);
  const [chartWindow, setChartWindow] = usePersistedWindow(
    "investmentsChartWindow",
    WindowType.ALL,
  );
  const [sort, setSort] = useState<SortState>({ key: "value", dir: "desc" });

  const [createInvestmentTrigger] = useCreateInvestmentMutation();

  const selectedYear =
    periodValue === ALL_TIME ? undefined : Number(periodValue);
  const periodLabel = periodValue === ALL_TIME ? "All time" : periodValue;

  const listQuery = useGetInvestmentsQuery({
    activeOnly: activeOnly.value,
    year: selectedYear,
  });
  const heroChartWindow = selectedYear === undefined ? chartWindow : null;
  const historyQuery = useGetInvestmentsHistoryQuery({
    activeOnly: activeOnly.value,
    window: heroChartWindow ?? undefined,
    year: selectedYear,
  });
  const { data: yearsData } = useGetInvestmentsYearsQuery();

  const investments = useMemo(
    () => listQuery.data?.investments ?? [],
    [listQuery.data],
  );
  const investmentsTotals = useMemo(
    () => investmentsTotalsOf(listQuery.data),
    [listQuery.data],
  );
  const history = historyQuery.currentData ?? EMPTY_HISTORY;
  const isChartLoading =
    historyQuery.isFetching && historyQuery.currentData === undefined;

  // A range switch only refetches the history, which the chart signals itself.
  const isLoading = listQuery.isFetching || historyQuery.isLoading;

  useEffect(() => {
    if (listQuery.data && historyQuery.data) {
      setIsInitialLoad(false);
    }
  }, [listQuery.data, historyQuery.data]);

  useEffect(() => {
    if (!yearsData) {
      return;
    }
    if (yearsData.length === 0) {
      // Keep the current year as a fallback so the period select never renders blank
      setYears(YEAR_OPTIONS);
      return;
    }
    const op: Options[] = yearsData.map((year) => ({
      label: year.toString(),
      value: year,
    }));
    setYears(op);
  }, [yearsData]);

  const handlePeriodChange = useCallback((value: string) => {
    setPeriodValue(value);
  }, []);

  // Reset to All time when the selected year drops out of the available list
  // (e.g. after deleting the only investment that reached back to that year).
  useEffect(() => {
    if (periodValue === ALL_TIME) {
      return;
    }
    if (!years.some((year) => year.value.toString() === periodValue)) {
      setPeriodValue(ALL_TIME);
    }
  }, [years, periodValue]);

  const handleActiveOnlyToggle = useCallback((value: boolean) => {
    const option = ACTIVE_OPTIONS.find(
      (activeOption) => activeOption.value === value,
    );
    setActiveOnly(option ?? ACTIVE_OPTIONS[0]);
  }, []);

  const handleClearFilters = useCallback(() => {
    setPeriodValue(ALL_TIME);
    setActiveOnly(ACTIVE_OPTIONS[1]);
  }, []);

  const navigateToInvestment = (id: number) => {
    setLocation("/investments/" + id);
  };

  const handleCreateInvestment = async (values: InvestmentFormValues) => {
    if (!values.trackingMode) {
      notify.error("Pick a tracking method before creating this investment.");
      return;
    }
    const payload: CreateInvestmentPayload = {
      name: values.name,
      institution: values.institution,
      investmentType: Number(values.investmentType?.value),
      startDate: formatDate(values.startDate),
      trackingMode: values.trackingMode.value,
      ...(values.trackingMode.value === "HOLDINGS_LEDGER" &&
        settlementPayloadOf(values)),
    };
    try {
      await createInvestmentTrigger(payload).unwrap();
      notify.success("Investment Created");
      setIsOpen(false);
    } catch {
      // Global middleware shows the toast for mutation failures.
    }
  };

  const periodOptions = useMemo(
    () => [
      { label: "All time", value: ALL_TIME },
      ...years.map((year) => ({
        label: year.label,
        value: year.value.toString(),
      })),
    ],
    [years],
  );

  const growth = useMemo(() => buildInvestmentsGrowth(history), [history]);

  const change = useMemo(
    () => headlineChangeOf(investmentsTotals),
    [investmentsTotals],
  );

  const heroGainBasis = useMemo(
    () => returnBasisOf(investments)?.gainTooltip ?? null,
    [investments],
  );

  const filterProps = useMemo(
    () => ({
      isCompact,
      periodValue,
      periodOptions,
      onPeriodChange: handlePeriodChange,
      activeOnly: activeOnly.value,
      onActiveOnlyChange: handleActiveOnlyToggle,
      count: investments.length,
      sort,
      onSortChange: setSort,
    }),
    [
      isCompact,
      periodValue,
      periodOptions,
      handlePeriodChange,
      handleActiveOnlyToggle,
      activeOnly.value,
      investments.length,
      sort,
    ],
  );

  if (listQuery.isError && !listQuery.data) {
    return (
      <Wrapper>
        <ErrorState
          message="Unable to load investments. Please try again."
          onRetry={async () => {
            await Promise.all(
              [listQuery, historyQuery].map((query) => query.refetch()),
            );
          }}
        />
      </Wrapper>
    );
  }

  // Full-page skeleton only on first load; filter refetch keep page mounted
  if (isLoading && isInitialLoad) {
    return <InvestmentsSkeleton />;
  }

  // After the first load the page stays mounted through filter refetches, so
  // surface an in-place progress hint rather than leaving it looking frozen.
  const isRefetching = isLoading && !isInitialLoad;

  const isEmpty = investments.length === 0;
  // The year list ignores the filters, so it tells "no match" apart from "no investments at all".
  const hasAnyInvestments = (yearsData?.length ?? 0) > 0;
  const isFilteredEmpty = isEmpty && hasAnyInvestments;
  const filtersVariant = isCompact ? "standalone" : "inline";

  const populatedView = (
    <>
      <InvestmentsHero
        periodLabel={periodLabel}
        totalValue={investmentsTotals.totalValue}
        portfolioValue={investmentsTotals.portfolioValue}
        valuation={listQuery.data?.valuation}
        investedValue={investmentsTotals.investedValue}
        brokerCosts={investmentsTotals.brokerCosts}
        accountFeeCosts={investmentsTotals.accountFeeCosts}
        interestCosts={investmentsTotals.interestCosts}
        adjustmentsNet={investmentsTotals.adjustmentsNet}
        changeValue={change.changeValue}
        changePercent={change.changePercent}
        changePercentStateReason={change.changePercentStateReason}
        gainBasis={heroGainBasis}
        investmentCount={investments.length}
        chartSeries={growth.series}
        chartXLabels={growth.xLabels}
        chartPoints={growth.points}
        chartWindow={heroChartWindow}
        isChartLoading={isChartLoading}
        chartWindowOptions={FILTER_OPTIONS}
        onChartWindowChange={setChartWindow}
        onCreateInvestment={() => setIsOpen(true)}
      />

      <InvestmentsFilters
        variant={filtersVariant}
        {...filterProps}
        showSort={true}
      />

      <InvestmentsInsights
        investments={investments}
        isCompact={isCompact}
        returnsByType={listQuery.data?.returnsByType ?? []}
        isValuationComplete={presentedTotalOf(investmentsTotals) !== null}
      />

      <InvestmentsHoldings
        investments={investments}
        byInvestment={history.byInvestment}
        onSelect={navigateToInvestment}
        sort={sort}
        onSortChange={setSort}
      />
    </>
  );

  // Surface the filter controls in the "no match" state so the period and
  // "Active only" toggle the copy refers to are actually reachable.
  const emptyFilters = isFilteredEmpty ? (
    <InvestmentsFilters
      variant={filtersVariant}
      {...filterProps}
      showSort={false}
    />
  ) : undefined;

  const emptyView = (
    <>
      <EmptyInvestmentsState
        isFiltered={isFilteredEmpty}
        onCreateInvestment={() => setIsOpen(true)}
        onClearFilters={handleClearFilters}
      />
      {emptyFilters}
    </>
  );

  const createPanel = isOpen ? (
    <InvestmentPanel
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      handleOnSubmit={handleCreateInvestment}
    />
  ) : null;

  // Surface the FAB only where the Hero's "Create investment" button is hidden:
  // the populated view on compact layouts. The empty state keeps its own button.
  const showCreateFab = isCompact && !isEmpty;

  const progress = isRefetching ? (
    <ProgressTrack role="progressbar" aria-label="Updating investments">
      <ProgressBar />
    </ProgressTrack>
  ) : null;

  return (
    <Wrapper aria-busy={isRefetching}>
      {progress}
      {isEmpty ? emptyView : populatedView}
      {showCreateFab ? (
        <CreateFab
          onClick={() => setIsOpen(true)}
          ariaLabel="Create investment"
        />
      ) : null}
      {createPanel}
    </Wrapper>
  );
};
